const HS_FORMS_HOST = 'https://api.hsforms.com'
const HS_REGION = (process.env.VUE_APP_HUBSPOT_REGION || '').toLowerCase()
const HUBSPOT_REGION_HOSTS: Record<string, string> = {
  eu1: 'https://forms-eu1.hsforms.com',
}
const HUBSPOT_SUBMISSION_BASE = HUBSPOT_REGION_HOSTS[HS_REGION] || HS_FORMS_HOST

const HUBSPOT_ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_adgroup',
  'utm_network',
  'utm_placement',
  'gclid',
  'fbclid',
]

export function showHubSpotCookieBanner(): void {
  if (typeof window === 'undefined') return

  const windowWithHsp = window as any
  windowWithHsp._hsp = windowWithHsp._hsp || []
  windowWithHsp._hsp.push(['showBanner'])
}

export function getHubspotUtk(): string | null {
  if (typeof document === 'undefined') return null

  const cookieString = document.cookie || ''
  if (!cookieString) return null

  const cookies = cookieString.split(';').map(part => part.trim())
  const hubspotCookie = cookies.find(cookie => cookie.startsWith('hubspotutk='))
  if (!hubspotCookie) return null

  const [, value = ''] = hubspotCookie.split('=')
  return value ? decodeURIComponent(value) : null
}

export type HSField = { name: string; value: string }
export type HSContext = { hutk?: string | null; pageName?: string; pageUri?: string }

export function buildHubspotContext(): HSContext {
  const pageName = (typeof document !== 'undefined' && document.title ? document.title : '') || ''
  const pageUri =
    (typeof window !== 'undefined' && window.location?.href ? window.location.href : '') || '/'

  return {
    hutk: getHubspotUtk() || '',
    pageName,
    pageUri,
  }
}

export type HSSubmitArgs = {
  portalId: string
  formId: string
  fields: HSField[]
  context?: HSContext
  legalConsentOptions?: any
}

export async function submitHubspotForm(args: HSSubmitArgs): Promise<any> {
  const { portalId, formId, fields, context, legalConsentOptions } = args

  if (!portalId || typeof portalId !== 'string' || !portalId.trim()) {
    throw new Error(
      'HubSpot form submission failed: portalId is required and must be a non-empty string.'
    )
  }
  if (!formId || typeof formId !== 'string' || !formId.trim()) {
    throw new Error(
      'HubSpot form submission failed: formId is required and must be a non-empty string.'
    )
  }

  const finalHost = HUBSPOT_SUBMISSION_BASE
  const url = `${finalHost}/submissions/v3/integration/submit/${portalId}/${formId}`

  const payload: Record<string, unknown> = {}
  if (fields && fields.length) payload.fields = fields

  const sanitizedContext = Object.entries(context || {}).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      if (value !== undefined && value !== '') {
        acc[key] = value
      }
      return acc
    },
    {}
  )
  if (Object.keys(sanitizedContext).length) payload.context = sanitizedContext

  if (legalConsentOptions && Object.keys(legalConsentOptions).length) {
    payload.legalConsentOptions = legalConsentOptions
  }

  const fetcher = typeof fetch === 'function' ? fetch : window?.fetch?.bind(window)
  if (!fetcher) throw new Error('Fetch API is not available')

  const response = await fetcher(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  const responseText = await response.text()

  let result: unknown
  try {
    result = responseText ? JSON.parse(responseText) : {}
  } catch {
    result = responseText
  }

  if (!response.ok) {
    const error = new Error(
      `HubSpot form submission failed with status ${response.status}: ${response.statusText}`
    )
    ;(error as unknown as Record<string, unknown>).response = result
    throw error
  }

  return result
}

export function mapAttributionToHSFields(attribution: Record<string, string>): HSField[] {
  if (!attribution) return []

  return HUBSPOT_ATTRIBUTION_KEYS.reduce<HSField[]>((acc, key) => {
    const value = attribution[key]
    if (value !== undefined && value !== null && String(value).trim().length) {
      acc.push({
        name: key,
        value: String(value),
      })
    }
    return acc
  }, [])
}
